import { useState, useEffect } from 'react';

import type { ContextIdentifierProvider } from '@atlaskit/editor-common/provider-factory';

import { useSpaceIdPromise } from './useSpaceIdPromise';

/**
 * Used to provide context for analytics events within the editor. This event is used by downstream services such as the
 * collaboration graph service. Downstream services are not aware of space keys, hence the space id is used.
 *
 * Note: This implementation is limited because we cannot update the container id when it changes mid-editing session, such as
 * if the page is moved to another space.
 */
export function useContextIdentifierProvider(
	spaceId: string | null | undefined,
	objectId: string,
	product: string = 'confluence',
) {
	const [contextIdentProvider, setContextIdentProvider] = useState<
		Promise<ContextIdentifierProvider> | undefined
	>(undefined);

	const spaceIdPromise = useSpaceIdPromise(spaceId);

	useEffect(() => {
		setContextIdentProvider(
			spaceIdPromise.then((containerId) => {
				return {
					containerId,
					objectId,
					product,
				};
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [spaceId, objectId, product]);

	return contextIdentProvider;
}
